/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListChallengesRequest,
  ListChallengesSecurity,
  QueryParamOutcome,
} from "../models/operations/listchallenges.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildChallengesQuery,
  ChallengesQueryData,
  prefetchChallenges,
  queryKeyChallenges,
} from "./challenges.core.js";
export {
  buildChallengesQuery,
  type ChallengesQueryData,
  prefetchChallenges,
  queryKeyChallenges,
};

export type ChallengesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listChallenges access
 *
 * @remarks
 * List authz challenge events from ClickHouse, enriched with resolution state from PostgreSQL.
 */
export function useChallenges(
  request?: ListChallengesRequest | undefined,
  security?: ListChallengesSecurity | undefined,
  options?: QueryHookOptions<ChallengesQueryData, ChallengesQueryError>,
): UseQueryResult<ChallengesQueryData, ChallengesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildChallengesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listChallenges access
 *
 * @remarks
 * List authz challenge events from ClickHouse, enriched with resolution state from PostgreSQL.
 */
export function useChallengesSuspense(
  request?: ListChallengesRequest | undefined,
  security?: ListChallengesSecurity | undefined,
  options?: SuspenseQueryHookOptions<ChallengesQueryData, ChallengesQueryError>,
): UseSuspenseQueryResult<ChallengesQueryData, ChallengesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildChallengesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setChallengesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      outcome?: QueryParamOutcome | undefined;
      principalUrn?: string | undefined;
      scope?: string | undefined;
      projectId?: string | undefined;
      resolved?: boolean | undefined;
      ids?: Array<string> | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      limit?: number | undefined;
      offset?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: ChallengesQueryData,
): ChallengesQueryData | undefined {
  const key = queryKeyChallenges(...queryKeyBase);

  return client.setQueryData<ChallengesQueryData>(key, data);
}

export function invalidateChallenges(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      outcome?: QueryParamOutcome | undefined;
      principalUrn?: string | undefined;
      scope?: string | undefined;
      projectId?: string | undefined;
      resolved?: boolean | undefined;
      ids?: Array<string> | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      limit?: number | undefined;
      offset?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listChallenges", ...queryKeyBase],
  });
}

export function invalidateAllChallenges(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listChallenges"],
  });
}
